// src/context/ProfileContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "services/supabaseClient";
import logo from "assets/greenland-compass-green.png";
import {
  clearActivityState,
  clearSessionLogoutFlags,
  isIdleSessionExpired,
} from "lib/sessionActivity";
import { isProfileComplete } from "lib/profileCompletion";

const ProfileContext = createContext();

export function useProfile() {
  return useContext(ProfileContext);
}

const FUNDPLANNER_SESSION_KEY = "fundplanner_session_v1";

const ORG_SCOPED_STORAGE_KEYS = [
  "debtstudio.session",
  "debtstudio.lastSessionBackup",
  FUNDPLANNER_SESSION_KEY,
  "recent_ds_deals",
];

function clearOrganizationScopedBrowserState() {
  try {
    ORG_SCOPED_STORAGE_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  } catch {
    // Storage can be unavailable in restricted browser environments.
  }
}

function clearRecentsState() {
  // Explicit known keys (safe, predictable)
  const knownRecentKeys = [
    "recent_ds_deals",
    // "recent_fp_funds",
    // "recent_tt_portfolios",
  ];

  knownRecentKeys.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
  });

  // Safety net: remove any "recent_*" keys
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("recent_")) {
        localStorage.removeItem(k);
      }
    });
  } catch {
    // ignore
  }
}

function clearAppSessionStateOnAuthBoundary(event) {
  // Requirement: nothing should be loaded upon login. Clear FundPlanner
  // session snapshot on SIGNED_IN and SIGNED_OUT so it cannot restore
  // a prior workspace when the tab remains open.
  if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
    try {
      sessionStorage.removeItem(FUNDPLANNER_SESSION_KEY);
    } catch {
      // ignore
    }
  }
}

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  /* ─────────────────────────────────────────────
   * Org context (authoritative)
   * ───────────────────────────────────────────── */
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgMode, setOrgMode] = useState(null); // 'single' | 'multi' | null
  const [mfaRequirement, setMfaRequirement] = useState({
    required: false,
    enrolled: false,
    setupRequired: false,
    requiredOrgs: [],
  });

  const navigate = useNavigate();
  const location = useLocation();

  const PUBLIC_PATHS = [
    "/",
    "/start",
    "/signup-user",
    "/update-password",
    "/reset-password",
    "/activate",
    "/set-password",
    "/disclaimer",
    "/terms",
    "/privacy",
    "/kb/support",
    "/session-expired",
  ];

  const todayISO = () => new Date().toISOString().slice(0, 10);

  const clearProfileState = useCallback(() => {
    setProfile(null);
    setAvailableOrgs([]);
    setSelectedOrg(null);
    setOrgMode(null);
    setMfaRequirement({
      required: false,
      enrolled: false,
      setupRequired: false,
      requiredOrgs: [],
    });
  }, []);

  /* ─────────────────────────────────────────────
   * Fetch orgs from org_user_links ONLY
   * ───────────────────────────────────────────── */
  const fetchUserOrgs = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from("org_user_links")
      .select(
        `
        org_id,
        mfa_required,
        mfa_required_at,
        orgs (
          id,
          name,
          tenant_id
        )
      `
      )
      .eq("user_id", userId)
      .lte("effective_from", todayISO())
      .or(`effective_to.is.null,effective_to.gte.${todayISO()}`);

    if (error) throw error;

    return (data || [])
      .filter((r) => r.orgs)
      .map((r) => ({
        ...r.orgs,
        mfa_required: !!r.mfa_required,
        mfa_required_at: r.mfa_required_at || null,
      }));
  }, []);

  /* ─────────────────────────────────────────────
   * Fetch profile + resolve org context
   * ───────────────────────────────────────────── */
  const fetchProfile = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    
    try {
      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();
      if (sessionErr) throw sessionErr;

      const user = session?.user;
      if (!user) {
        clearProfileState();

        const wasInactivity =
          localStorage.getItem("compass.inactivityLogout") === "1";
        const isPublic = PUBLIC_PATHS.includes(window.location.pathname);
        if (wasInactivity && !isPublic) {
          setRedirecting(true);
          navigate("/session-expired", { replace: true });
          return;
        }

        if (isPublic) {
          clearSessionLogoutFlags();
        }

        if (!isPublic) {
          setRedirecting(true);
          navigate("/", { replace: true });
        }
        return;
      }

      const needsInitialPassword =
        !!user.app_metadata?.activation_pending &&
        !user.app_metadata?.initial_password_set_at;
      if (needsInitialPassword && window.location.pathname !== "/set-password") {
        setRedirecting(true);
        navigate("/set-password", { replace: true });
        return;
      }

      if (isIdleSessionExpired()) {
        try {
          localStorage.setItem("compass.inactivityLogout", "1");
          localStorage.removeItem("compass.manualLogout");
          clearActivityState();
        } catch {
          // ignore storage failures
        }

        clearProfileState();
        setLoading(false);

        try {
          await supabase.auth.signOut();
        } catch {
          // ignore sign-out failures
        }

        navigate("/session-expired", { replace: true });
        return;
      }

      const { data: profileData, error: profileErr } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (profileErr) {
        throw profileErr;
      }

      if (!profileData) {
        setProfile(null);
        const isPublic = PUBLIC_PATHS.includes(window.location.pathname);
        const onSignupPage = window.location.pathname === "/signup-user";
        if (!onSignupPage && !isPublic) {
          setRedirecting(true);
          navigate("/signup-user", { replace: true });
        }
        return;
      }

      if (profileData.disabled) {
        try {
          localStorage.setItem("compass.disabledAccount", "1");
          clearActivityState();
          await supabase.auth.signOut();
        } catch {
          // ignore sign-out/storage failures
        }

        setProfile(null);
        setAvailableOrgs([]);
        setSelectedOrg(null);
        setOrgMode(null);
        setMfaRequirement({
          required: false,
          enrolled: false,
          setupRequired: false,
          requiredOrgs: [],
        });
        setRedirecting(true);
        navigate("/", { replace: true });
        return;
      }

      setProfile(profileData);

      const isIncomplete = !isProfileComplete(profileData);

      const onSignupPage = window.location.pathname === "/signup-user";
      const isPublic = PUBLIC_PATHS.includes(window.location.pathname);

      if (isIncomplete && !onSignupPage && !isPublic) {
        setRedirecting(true);
        navigate("/signup-user", { replace: true });
        return;
      }

      if (!isIncomplete && onSignupPage) {
        setRedirecting(true);
        navigate("/dashboard", { replace: true });
        return;
      }

      /* ─────────────────────────────────────────────
       * Resolve org context (NO admin branching)
       * ───────────────────────────────────────────── */
      const orgs = await fetchUserOrgs(user.id);
      const requiredMfaOrgs = orgs.filter((org) => org.mfa_required);
      const mfaRequired = requiredMfaOrgs.length > 0;
      let mfaEnrolled = false;

      if (mfaRequired) {
        const { data: factors, error: factorsError } =
          await supabase.auth.mfa.listFactors();

        if (factorsError) throw factorsError;

        mfaEnrolled = (factors?.totp || []).some(
          (factor) => factor.status === "verified"
        );
      }

      const setupRequired = mfaRequired && !mfaEnrolled;

      setMfaRequirement({
        required: mfaRequired,
        enrolled: mfaEnrolled,
        setupRequired,
        requiredOrgs: requiredMfaOrgs,
      });

      if (setupRequired && window.location.pathname !== "/mfa-required") {
        setRedirecting(true);
        navigate("/mfa-required", { replace: true });
        return;
      }

      setAvailableOrgs(orgs);

      if (orgs.length === 1) {
        setSelectedOrg(orgs[0]);
        setOrgMode("single");
        localStorage.removeItem("selectedOrgId");
      } else if (orgs.length > 1) {
        setOrgMode("multi");

        const storedOrgId = localStorage.getItem("selectedOrgId");
        const restored = storedOrgId && orgs.find((o) => o.id === storedOrgId);

        setSelectedOrg(restored || null);
      } else {
        // zero-org user
        setSelectedOrg(null);
        setOrgMode(null);
        localStorage.removeItem("selectedOrgId");
      }
    } catch {
      clearProfileState();
      clearActivityState();
      clearSessionLogoutFlags();

      try {
        await supabase.auth.signOut();
      } catch {
        // A local reset and navigation still recover the UI if sign-out fails.
      }

      navigate("/start", { replace: true });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [navigate, fetchUserOrgs, clearProfileState]);

  /* ─────────────────────────────────────────────
   * Auth listener
   * ───────────────────────────────────────────── */
  useEffect(() => {
    let mounted = true;

    (async () => {
      if (mounted) await fetchProfile(true);
    })();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        // Clear FundPlanner workspace + recents on auth boundaries
        if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
          clearRecentsState();
          clearAppSessionStateOnAuthBoundary(event);
        }

        if (session?.user) {
          // Auth maintenance events (for example TOKEN_REFRESHED when a hidden
          // tab resumes) must not replace Layout's Outlet and unmount the
          // active application workspace.
          fetchProfile(false);
        } else {
          clearProfileState();
          setLoading(false);

          const wasInactivity =
            localStorage.getItem("compass.inactivityLogout") === "1";
          const isPublic = PUBLIC_PATHS.includes(window.location.pathname);
          if (wasInactivity && !isPublic) {
            navigate("/session-expired", { replace: true });
            return;
          }

          if (isPublic) {
            clearSessionLogoutFlags();
          }

          if (!isPublic) {
            setRedirecting(true);
            navigate("/", { replace: true });
          }
        }
      }
    );

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [fetchProfile, navigate, clearProfileState]);

  /* ─────────────────────────────────────────────
   * Clear redirecting flag on route change
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (redirecting) setRedirecting(false);
  }, [location.pathname]);

  useEffect(() => {
    if (loading || !profile) return;
    if (!mfaRequirement.setupRequired) return;
    if (location.pathname === "/mfa-required") return;

    setRedirecting(true);
    navigate("/mfa-required", { replace: true });
  }, [
    loading,
    profile,
    mfaRequirement.setupRequired,
    location.pathname,
    navigate,
  ]);

  /* ─────────────────────────────────────────────
   * Org selection (multi-org only)
   * ───────────────────────────────────────────── */
  const selectOrg = useCallback(
    (org) => {
      if (orgMode !== "multi") return;

      const currentOrgId = selectedOrg?.id == null ? "" : String(selectedOrg.id);
      const nextOrgId = org?.id == null ? "" : String(org.id);
      if (currentOrgId === nextOrgId) return;

      // Organization selection is a hard workspace boundary. Clear snapshots
      // before publishing the new organization so an unmounted module cannot
      // restore records belonging to the prior organization.
      clearOrganizationScopedBrowserState();

      if (!org) {
        setSelectedOrg(null);
        localStorage.removeItem("selectedOrgId");
        return;
      }
      setSelectedOrg(org);
      localStorage.setItem("selectedOrgId", org.id);
    },
    [orgMode, selectedOrg?.id]
  );

  /* ─────────────────────────────────────────────
   * Redirecting screen
   * ───────────────────────────────────────────── */
  if (redirecting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-app text-app">
        <img
          src={logo}
          alt="CoreCompass Logo"
          className="h-16 w-16 mb-4 animate-pulse"
        />
        <p className="text-lg font-medium text-brand1">Redirecting…</p>
        <div className="mt-4 h-6 w-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <ProfileContext.Provider
      value={{
        profile,
        loading,
        refreshProfile: fetchProfile,

        // org context (DS / FP authoritative)
        availableOrgs,
        selectedOrg,
        orgMode,
        selectOrg,
        mfaRequirement,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}
